import moment from 'moment';
import { APP } from 'src/app/config/app';
import {
  IVehicleScheduleAvailability,
  IVehicleScheduleChange,
  IVehicleScheduleSaveSummary,
  IVehicleScheduleSummaryRow,
  IVehicleScheduleSummaryRows,
  IVehicleScheduleTrip,
  IVehicleScheduleUpdateFailure,
  IVehicleScheduleUpdateOutcome,
} from 'src/app/models';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import { scheduleServiceLabel } from './scheduleChanges';

function timeRange(startDateTime: Date, endDateTime: Date): string {
  const start = moment(startDateTime).format(APP.SCHEDULE_SUMMARY_START_FORMAT);
  return `${start} - ${moment(endDateTime).format(APP.SCHEDULE_SUMMARY_END_FORMAT)}`;
}

function availabilityRow(availability: IVehicleScheduleAvailability): IVehicleScheduleSummaryRow {
  return {
    key: `availability-${availability.id}`,
    title: availability.route?.name || `#${availability.id}`,
    service: availability.service,
    serviceLabel: scheduleServiceLabel(availability.service) || undefined,
    timeRange: timeRange(availability.startDateTime, availability.endDateTime),
    driver: availability.driver?.name,
    vehicle: availability.vehicle?.name,
    causedFailure: false,
  };
}

function tripRow(trip: IVehicleScheduleTrip, availability?: IVehicleScheduleAvailability): IVehicleScheduleSummaryRow {
  return {
    key: `trip-${trip.id}`,
    title: trip.route?.name || `#${trip.id}`,
    service: availability?.service,
    serviceLabel: scheduleServiceLabel(availability?.service) || undefined,
    timeRange: timeRange(trip.startDateTime, trip.endDateTime),
    driver: trip.driver?.name,
    vehicle: trip.vehicle?.name,
    causedFailure: false,
  };
}

function unknownRow(keyPrefix: string, id: number): IVehicleScheduleSummaryRow {
  return { key: `${keyPrefix}-${id}`, title: `#${id}`, timeRange: '', causedFailure: false };
}

function changedTrips(change: IVehicleScheduleChange): IVehicleScheduleTrip[] {
  return change.availability.trips.filter((trip) => trip.id !== undefined && change.changedTripIds.includes(trip.id));
}

function failedRows(
  failure: IVehicleScheduleUpdateFailure,
  change: IVehicleScheduleChange | undefined,
  tripsById: Map<number, { trip: IVehicleScheduleTrip; availability: IVehicleScheduleAvailability }>,
): IVehicleScheduleSummaryRows {
  const errorCode = failure.error?.errorCode ?? 0;
  const tripCausedFailure = failure.tripId != null;
  const causeReason = { errorCode, causedFailure: errorCode !== ErrorsCode.SCHEDULE_LINKED_CHANGE_FAILED };
  const linkedReason = { errorCode: ErrorsCode.SCHEDULE_LINKED_CHANGE_FAILED, causedFailure: false };
  const availabilityReason = tripCausedFailure ? linkedReason : causeReason;
  const availabilityShown = change?.availabilityChanged || availabilityReason.causedFailure;
  const availabilities = availabilityShown
    ? [{ ...(change ? availabilityRow(change.availability) : unknownRow('availability', failure.availabilityId)),
      ...availabilityReason }]
    : [];
  const tripIds = new Set(change ? changedTrips(change).map((trip) => trip.id!) : []);
  if (failure.tripId != null) {
    tripIds.add(failure.tripId);
  }
  const trips = [...tripIds].map((tripId) => {
    const found = tripsById.get(tripId);
    const row = found ? tripRow(found.trip, found.availability) : unknownRow('trip', tripId);
    return { ...row, ...(tripId === failure.tripId ? causeReason : linkedReason) };
  });
  return { availabilities, trips };
}

export function scheduleSaveSummary(
  outcome: IVehicleScheduleUpdateOutcome,
  changes: IVehicleScheduleChange[],
): IVehicleScheduleSaveSummary {
  const changesById = new Map(changes.map((change) => [change.availability.id, change]));
  const tripsById = new Map(
    changes.flatMap((change) =>
      change.availability.trips
        .filter((trip) => trip.id !== undefined)
        .map((trip) => [trip.id!, { trip, availability: change.availability }] as const),
    ),
  );
  const succeededAvailabilityIds = new Set(outcome.succeeded);
  const succeededChanges = changes.filter((change) => succeededAvailabilityIds.has(change.availability.id));
  const failed = outcome.failures.map((failure) =>
    failedRows(failure, changesById.get(failure.availabilityId), tripsById),
  );
  return {
    succeeded: {
      availabilities: succeededChanges
        .filter((change) => change.availabilityChanged)
        .map((change) => availabilityRow(change.availability)),
      trips: succeededChanges.flatMap((change) =>
        changedTrips(change).map((trip) => tripRow(trip, change.availability)),
      ),
    },
    failed: {
      availabilities: failed.flatMap((rows) => rows.availabilities),
      trips: failed.flatMap((rows) => rows.trips),
    },
  };
}
